import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// supabase.functions.invoke returns a generic "non-2xx status code" error
// whose Response body (with our {error: "..."} detail) is tucked into
// `context`. Try to read it so toasts show the real reason.
async function functionErrorDetail(err: unknown): Promise<string> {
  const fallback = (err as { message?: string })?.message ?? "Unknown error";
  const ctx = (err as { context?: Response }).context;
  if (!ctx || typeof ctx.json !== "function") return fallback;
  try {
    const body = await ctx.clone().json();
    if (body && typeof body === "object" && typeof body.error === "string") {
      return body.error;
    }
  } catch {
    // Not JSON — fall through.
  }
  return fallback;
}

interface GrantTierVars {
  user_id: string;
  tier_id: string;
  expires_at: string | null;
  note: string | null;
  pause_stripe: boolean;
}

interface RevokeGrantVars {
  grant_id: string;
  user_id: string;
}

export function useGrantTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: GrantTierVars) => {
      const { data, error } = await supabase.functions.invoke(
        "admin-grant-tier",
        { body: vars },
      );
      if (error) {
        const detail = await functionErrorDetail(error);
        throw new Error(detail);
      }
      if (data && typeof data === "object" && "error" in data) {
        throw new Error((data as { error: string }).error);
      }
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["admin-grants"] });
      void qc.invalidateQueries({ queryKey: ["admin-user-billing", vars.user_id] });
    },
  });
}

export function useRevokeGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: RevokeGrantVars) => {
      const { data, error } = await supabase.functions.invoke(
        "admin-revoke-grant",
        { body: { grant_id: vars.grant_id } },
      );
      if (error) {
        const detail = await functionErrorDetail(error);
        throw new Error(detail);
      }
      if (data && typeof data === "object" && "error" in data) {
        throw new Error((data as { error: string }).error);
      }
      return data as { to_tier_id?: string } | null;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["admin-grants"] });
      void qc.invalidateQueries({ queryKey: ["admin-user-billing", vars.user_id] });
    },
  });
}
